import { motion } from "framer-motion";
import { Check, Star } from "@phosphor-icons/react";
import StoreBadges from "@/components/StoreBadges";

/**
 * Pricing section — two tiers side by side.
 *
 * Visual hierarchy is trial-first, price-second (mobile visitors used to
 * see a giant "$9.99" hit them before they'd read a single benefit).
 * Now the "14-day free trial" phrase is the tier's headline; the actual
 * per-month/per-year rate is present but sits as small supporting type.
 *
 * - Monthly $9.99 / Yearly $79.99 (annual highlighted as "Best value")
 * - Founding Member framing: rate locked in for life per Apple's IAP price-lock
 * - CTA points at the App Store via the existing StoreBadges component so
 *   analytics + Meta Pixel + Google Ads Lead events fire consistently.
 */
const TIERS = [
  {
    id: "monthly",
    label: "Monthly plan",
    price: "$9.99",
    period: "/mo after trial",
    subline: "Cancel anytime · billed monthly",
    highlight: false,
    testId: "pricing-tier-monthly",
  },
  {
    id: "yearly",
    label: "Yearly plan",
    price: "$79.99",
    period: "/yr after trial",
    subline: "Effective $6.67/mo · save 33%",
    highlight: true,
    badge: "Best value · Founding Member",
    testId: "pricing-tier-yearly",
  },
];

const INCLUDED = [
  "50+ clinical tools",
  "AI clinical answers (safety-checked)",
  "Weight-based Peds Dose Calc",
  "Parkland burn fluid formula",
  "COWS & alcohol-withdrawal scoring",
  "ECG interpretation aid",
  "Drug-drug interaction checker",
  "15 bedside calculators (GCS, MEWS, NEWS2, NIHSS, CrCl, and more)",
  "Onboarding tour + smart tool discovery",
  "Every new tool we ship — no upsell",
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="relative border-t border-white/10 py-24 md:py-32 px-5 md:px-10"
      data-testid="pricing-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-3xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
            // pricing
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
            14 days free.
            <br />
            <span className="text-white/60">Then lock in your rate for life.</span>
          </h2>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed">
            No credit card to start. Full access during the trial. When you
            subscribe during launch, your <strong className="text-white">Founding Member rate</strong>{" "}
            is guaranteed to stay the same for as long as your subscription
            stays continuous — even after we raise prices for new users.
          </p>
        </div>

        {/* Tiers */}
        <div className="mt-14 grid md:grid-cols-2 gap-5 max-w-4xl">
          {TIERS.map((tier, idx) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className={`relative border p-8 md:p-10 flex flex-col ${
                tier.highlight
                  ? "border-[#FF2A2A]/60 bg-[#FF2A2A]/[0.04]"
                  : "border-white/12 bg-black"
              }`}
              data-testid={tier.testId}
            >
              {tier.badge && (
                <div className="absolute -top-3 left-8 flex items-center gap-1.5 bg-[#FF2A2A] text-white font-mono text-[9px] uppercase tracking-[0.22em] px-2.5 py-1">
                  <Star size={10} weight="fill" />
                  {tier.badge}
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                {tier.label}
              </div>

              {/* Trial-first hero — visitors read "14 days free" before
                  they ever see a dollar sign. */}
              <div className="mt-3">
                <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.02] text-white">
                  14 days{" "}
                  <span className={tier.highlight ? "text-[#FF2A2A]" : "italic font-light text-white/70"}>
                    free
                  </span>
                </div>
              </div>

              {/* Price sits as small supporting type — visible for App
                  Store compliance but no longer competing with the
                  benefit for the visitor's first glance. */}
              <div className="mt-4 flex items-baseline gap-1.5">
                <span
                  className="font-mono text-lg font-semibold text-white/85 tabular-nums"
                  data-testid={`${tier.testId}-price`}
                >
                  {tier.price}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">
                  {tier.period}
                </span>
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                {tier.subline}
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 flex-1">
                <p className="text-white/70 text-sm leading-relaxed">
                  Start using every clinical tool immediately. Cancel any time
                  in <span className="text-white">Settings → Subscriptions</span>{" "}
                  before day 14 and you won&rsquo;t be charged.
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 max-w-4xl">
          <StoreBadges testIdPrefix="pricing-store" source="pricing" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
            Charged to your Apple ID · auto-renews · cancel in iOS Settings
          </p>
        </div>

        {/* What's included */}
        <div className="mt-20 md:mt-24 max-w-4xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
            // both plans include
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {INCLUDED.map((line) => (
              <li
                key={line}
                className="flex items-start gap-3 text-sm text-white/80"
              >
                <Check
                  size={14}
                  weight="bold"
                  className="text-[#FF2A2A] mt-1 shrink-0"
                />
                <span className="leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
